import React, {ReactNode, RefObject, useContext, useRef, useState} from "react";
import {Modal} from "reactstrap";
import UserContext from "../../../context/UserContext";
import {BrandUserContextType} from "../../../ui-kit/src/context/CurrentUserContext";
import styles from "./SettingsModal.module.scss";
import {isEmailFormat} from "../../../ui-kit/src/stringUtils";
import updateUserMutation from "../../../mutations/UpdateUserMutation";
import updateBrandMutation from "../../../mutations/UpdateBrandMutation";
import changePasswordMutation from "../../../mutations/ChangePasswordMutation";
import environment from "../../../Environment";
import {ourToast} from "../../../ui-kit/src/atoms/Toast";

type Section = "profile" | "company" | "password";
type SectionErrors = Record<Section, string | null>;

const PROFILE: Section = "profile";
const COMPANY: Section = "company";
const PASSWORD: Section = "password";
const SECTION_ORDER: Section[] = [PROFILE, COMPANY, PASSWORD];
const MIN_PASSWORD_LENGTH = 6;
const EMPTY_ERRORS: SectionErrors = {profile: null, company: null, password: null};

type SavedValues = {
  firstName: string,
  lastName: string,
  email: string,
  brandName: string,
  logo: string | null,
}

type MutationRunner<TInput, TResponse> = (
  env: typeof environment,
  input: TInput,
  onSuccess: (response: TResponse) => void,
  onError: (err: Error[]) => void,
) => void;

function getDisplayId(id: string | null | undefined): string | null {
  if (!id) {
    return null;
  }
  try {
    const parts = atob(id).split(":");
    if (parts.length > 1) {
      return `core:user:${parts[parts.length - 1]}`;
    }
  } catch (e) {
    // Not a base64 relay id, show it as it is
  }
  return id;
}

function getInitials(firstName: string, lastName: string, email: string): string {
  const initials = [firstName, lastName]
    .filter(part => !!part && part.trim().length > 0)
    .map(part => part.trim().charAt(0))
    .join("");
  if (initials) {
    return initials.toUpperCase();
  }
  return email ? email.charAt(0).toUpperCase() : "?";
}

function getErrorMessage(err: Error[] | null | undefined): string {
  if (err && err[0] && err[0].message) {
    return err[0].message;
  }
  return "An error occurred. Try again.";
}

function runMutation<TInput, TResponse>(mutation: MutationRunner<TInput, TResponse>, input: TInput): Promise<TResponse> {
  return new Promise((resolve, reject) => {
    mutation(environment, input, resolve, err => reject(getErrorMessage(err)));
  });
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

type SettingsModalProps = {
  isOpen: boolean,
  toggle: () => void,
  isAdmin?: boolean,
}

export default function SettingsModal(props: SettingsModalProps) {
  const user = useContext(UserContext);
  if (!user) {
    return null;
  }
  return <SettingsModalContent {...props} user={user}/>;
}

function SettingsModalContent({isOpen, toggle, isAdmin = true, user}: SettingsModalProps & { user: BrandUserContextType }) {
  const canEditCompany = isAdmin;

  const [saved, setSaved] = useState<SavedValues>({
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    email: user.email || "",
    brandName: user.brand.name || "",
    logo: user.brand.logo || null,
  });
  const [firstName, setFirstName] = useState(saved.firstName);
  const [lastName, setLastName] = useState(saved.lastName);
  const [email, setEmail] = useState(saved.email);
  const [brandName, setBrandName] = useState(saved.brandName);
  const [logo, setLogo] = useState<string | null>(saved.logo);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [errors, setErrors] = useState<SectionErrors>(EMPTY_ERRORS);
  const [isSaving, setIsSaving] = useState(false);
  const [activeSection, setActiveSection] = useState<Section>(PROFILE);
  const [isIdCopied, setIsIdCopied] = useState(false);
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);

  const bodyRef = useRef<HTMLDivElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const profileRef = useRef<HTMLElement>(null);
  const companyRef = useRef<HTMLElement>(null);
  const passwordRef = useRef<HTMLElement>(null);
  const sectionRefs: Record<Section, RefObject<HTMLElement>> = {
    profile: profileRef,
    company: companyRef,
    password: passwordRef,
  };

  const displayId = getDisplayId(user.id);

  const isProfileDirty = firstName !== saved.firstName || lastName !== saved.lastName || email !== saved.email;
  const isCompanyDirty = canEditCompany && (brandName !== saved.brandName || logo !== saved.logo);
  const isPasswordDirty = password.length > 0 || confirmPassword.length > 0;
  const dirtyBySection: Record<Section, boolean> = {
    profile: isProfileDirty,
    company: isCompanyDirty,
    password: isPasswordDirty,
  };
  const isDirty = isProfileDirty || isCompanyDirty || isPasswordDirty;
  const hasErrors = SECTION_ORDER.some(section => !!errors[section]);

  const setSectionError = (section: Section, message: string | null) => {
    setErrors(current => ({...current, [section]: message}));
  };

  const clearSectionError = (section: Section) => {
    if (errors[section]) {
      setSectionError(section, null);
    }
  };

  const scrollToSection = (section: Section) => {
    const body = bodyRef.current;
    const element = sectionRefs[section].current;
    setActiveSection(section);
    if (body && element) {
      body.scrollTo({top: Math.max(element.offsetTop - 16, 0), behavior: "smooth"});
    }
  };

  const onBodyScroll = () => {
    const body = bodyRef.current;
    if (!body) {
      return;
    }
    if (body.scrollTop + body.clientHeight >= body.scrollHeight - 4) {
      setActiveSection(SECTION_ORDER[SECTION_ORDER.length - 1]);
      return;
    }
    let current = SECTION_ORDER[0];
    SECTION_ORDER.forEach(section => {
      const element = sectionRefs[section].current;
      if (element && element.offsetTop <= body.scrollTop + 48) {
        current = section;
      }
    });
    setActiveSection(current);
  };

  const close = () => {
    if (isSaving) {
      return;
    }
    toggle();
  };

  const copyId = () => {
    if (!displayId || !navigator.clipboard) {
      return;
    }
    navigator.clipboard.writeText(displayId).then(() => {
      setIsIdCopied(true);
      setTimeout(() => setIsIdCopied(false), 1600);
    });
  };

  const openLogoPicker = () => {
    if (logoInputRef.current) {
      logoInputRef.current.click();
    }
  };

  const onLogoFile = (file: File | null | undefined) => {
    if (!file) {
      return;
    }
    if (file.type.split("/")[0] !== "image") {
      setSectionError(COMPANY, "Choose an image file, such as PNG, JPG or SVG.");
      return;
    }
    readFileAsDataUrl(file)
      .then(result => {
        clearSectionError(COMPANY);
        setLogo(result);
      })
      .catch(() => setSectionError(COMPANY, "The file cannot be read. Choose a different image."));
  };

  const validate = (): SectionErrors => {
    const nextErrors: SectionErrors = {...EMPTY_ERRORS};
    if (isProfileDirty && !isEmailFormat(email)) {
      nextErrors.profile = "Enter a valid email address, for example name@company.com.";
    }
    if (isCompanyDirty && brandName.trim().length === 0) {
      nextErrors.company = "Enter a company name.";
    }
    if (isPasswordDirty) {
      if (password.length < MIN_PASSWORD_LENGTH) {
        nextErrors.password = `Password must contain at least ${MIN_PASSWORD_LENGTH} characters.`;
      } else if (password !== confirmPassword) {
        nextErrors.password = "The passwords are not the same. Enter the new password again to confirm it.";
      }
    }
    return nextErrors;
  };

  const save = () => {
    const nextErrors = validate();
    setErrors(nextErrors);
    const firstInvalidSection = SECTION_ORDER.find(section => !!nextErrors[section]);
    if (firstInvalidSection) {
      scrollToSection(firstInvalidSection);
      return;
    }

    const tasks: Promise<void>[] = [];
    let failed = false;

    if (isProfileDirty) {
      const values = {firstName, lastName, email};
      tasks.push(runMutation(updateUserMutation as MutationRunner<typeof values, unknown>, values)
        .then(() => setSaved(current => ({...current, ...values})))
        .catch((message: string) => {
          failed = true;
          setSectionError(PROFILE, message);
        }));
    }

    if (isCompanyDirty) {
      const isLogoChanged = logo !== saved.logo;
      const input = {
        brand: user.brand.id,
        name: brandName,
        ...(isLogoChanged ? {logo} : {}),
      };
      type BrandResponse = { updateBrand: { brand: { name: string, logo: string | null } | null } | null };
      tasks.push(runMutation(updateBrandMutation as MutationRunner<typeof input, BrandResponse>, input)
        .then(res => {
          const savedBrand = res && res.updateBrand && res.updateBrand.brand ? res.updateBrand.brand : null;
          const savedLogo = savedBrand ? savedBrand.logo : logo;
          const savedName = savedBrand ? savedBrand.name : brandName;
          setLogo(savedLogo);
          setBrandName(savedName);
          setSaved(current => ({...current, brandName: savedName, logo: savedLogo}));
        })
        .catch((message: string) => {
          failed = true;
          setSectionError(COMPANY, message);
        }));
    }

    if (isPasswordDirty) {
      const input = {password, confirmPassword};
      tasks.push(runMutation(changePasswordMutation as MutationRunner<typeof input, unknown>, input)
        .then(() => {
          setPassword("");
          setConfirmPassword("");
          setShowPassword(false);
        })
        .catch((message: string) => {
          failed = true;
          setSectionError(PASSWORD, message);
        }));
    }

    setIsSaving(true);
    Promise.all(tasks).then(() => {
      setIsSaving(false);
      if (failed) {
        ourToast("error", "Some changes are not saved");
      } else {
        ourToast("success", "Changes saved");
      }
    });
  };

  const getNavStatus = (section: Section): string => {
    if (errors[section]) {
      return "Needs attention";
    }
    if (dirtyBySection[section]) {
      return "Unsaved changes";
    }
    if (section === PROFILE) {
      return saved.email || "Name and email";
    }
    if (section === COMPANY) {
      if (!canEditCompany) {
        return "View only";
      }
      return saved.logo ? "Name and logo set" : "No logo yet";
    }
    return "Unchanged";
  };

  const navItems: { section: Section, label: string, icon: string }[] = [
    {section: PROFILE, label: "Profile", icon: "fa-regular fa-user"},
    {section: COMPANY, label: "Company", icon: "fa-regular fa-building"},
    {section: PASSWORD, label: "Password", icon: "fa-regular fa-lock"},
  ];

  const passwordsMatch = confirmPassword.length > 0 && password === confirmPassword;
  const passwordsMismatch = confirmPassword.length > 0 && password !== confirmPassword;

  let confirmHint: ReactNode = "Re-enter the new password.";
  if (passwordsMatch) {
    confirmHint = <span className={styles.hintSuccess}>
      <i className="fa-regular fa-check" aria-hidden="true"/>Passwords match
    </span>;
  } else if (passwordsMismatch) {
    confirmHint = <span className={styles.hintWarning}>
      <i className="fa-regular fa-xmark" aria-hidden="true"/>The passwords are not the same
    </span>;
  }

  let footerStatus: ReactNode;
  if (isSaving) {
    footerStatus = <span className={`${styles.status} ${styles.statusSaving}`}>
      <i className="fa-regular fa-spinner-third fa-spin" aria-hidden="true"/>Saving changes…
    </span>;
  } else if (hasErrors) {
    footerStatus = <span className={`${styles.status} ${styles.statusError}`}>
      <i className="fa-regular fa-circle-exclamation" aria-hidden="true"/>Fix the highlighted sections to save
    </span>;
  } else if (isDirty) {
    footerStatus = <span className={`${styles.status} ${styles.statusDirty}`}>
      <span className={styles.statusDot}/>Unsaved changes
    </span>;
  } else {
    footerStatus = <span className={`${styles.status} ${styles.statusSaved}`}>
      <span className={styles.statusDot}/>All changes saved
    </span>;
  }

  return <Modal isOpen={isOpen}
                toggle={close}
                backdrop={isDirty || isSaving ? "static" : true}
                keyboard={!isDirty && !isSaving}
                className={styles.modal}
                contentClassName={styles.content}>
    <header className={styles.header}>
      <div className={styles.headerText}>
        <span className={styles.eyebrow}>Account</span>
        <h2 className={styles.title}>Account settings</h2>
        <p className={styles.subtitle}>Manage your profile, company details and password.</p>
      </div>
      <div className={styles.headerActions}>
        {displayId && <button type="button"
                              className={styles.idChip}
                              onClick={copyId}
                              title="Copy user ID"
                              aria-label={`Copy user ID ${displayId}`}>
          <span className={styles.idLabel}>ID</span>
          <span className={styles.idValue}>{displayId}</span>
          <i className={isIdCopied ? "fa-regular fa-check" : "fa-regular fa-copy"} aria-hidden="true"/>
        </button>}
        <button type="button" className={styles.iconButton} onClick={close} aria-label="Close" disabled={isSaving}>
          <i className="fa-regular fa-xmark" aria-hidden="true"/>
        </button>
      </div>
    </header>

    <div className={styles.layout}>
      <aside className={styles.sidebar}>
        <div className={styles.userCard}>
          <span className={styles.avatar}>
            {user.avatar
              ? <img src={user.avatar} alt=""/>
              : getInitials(saved.firstName, saved.lastName, saved.email)}
          </span>
          <div className={styles.userCardText}>
            <strong>{`${saved.firstName} ${saved.lastName}`.trim() || "Your account"}</strong>
            <span>{saved.email}</span>
          </div>
        </div>
        <nav className={styles.nav} aria-label="Account settings sections">
          {navItems.map(item => {
            const isActive = activeSection === item.section;
            const hasError = !!errors[item.section];
            const isItemDirty = dirtyBySection[item.section];
            return <button key={item.section}
                           type="button"
                           className={[
                             styles.navItem,
                             isActive ? styles.navItemActive : "",
                             hasError ? styles.navItemError : "",
                           ].join(" ")}
                           aria-current={isActive ? "true" : undefined}
                           onClick={() => scrollToSection(item.section)}>
              <span className={styles.navIcon}><i className={item.icon} aria-hidden="true"/></span>
              <span className={styles.navText}>
                <strong>{item.label}</strong>
                <span>{getNavStatus(item.section)}</span>
              </span>
              {hasError && <i className={`fa-solid fa-circle-exclamation ${styles.navErrorIcon}`} aria-hidden="true"/>}
              {!hasError && isItemDirty && <span className={styles.navDirtyDot} aria-hidden="true"/>}
            </button>
          })}
        </nav>
      </aside>

      <div className={styles.body} ref={bodyRef} onScroll={onBodyScroll}>
        <section ref={profileRef} className={styles.section} aria-labelledby="settings-profile-title">
          <SectionHeader id="settings-profile-title"
                         icon="fa-regular fa-user"
                         title="Profile"
                         description="Your name and the email address you use to sign in."/>
          <div className={styles.sectionBody}>
            {errors.profile && <SectionError message={errors.profile}/>}
            <div className={styles.fieldGrid}>
              <Field id="first-name-input" label="First name">
                <input id="first-name-input"
                       type="text"
                       className={styles.input}
                       autoComplete="given-name"
                       value={firstName}
                       onChange={e => {
                         clearSectionError(PROFILE);
                         setFirstName(e.target.value);
                       }}/>
              </Field>
              <Field id="last-name-input" label="Last name">
                <input id="last-name-input"
                       type="text"
                       className={styles.input}
                       autoComplete="family-name"
                       value={lastName}
                       onChange={e => {
                         clearSectionError(PROFILE);
                         setLastName(e.target.value);
                       }}/>
              </Field>
            </div>
            <Field id="email-input" label="Email address" hint="Used to sign in and for account notifications.">
              <div className={`${styles.inputWrap} ${errors.profile ? styles.inputWrapInvalid : ""}`}>
                <i className={`fa-regular fa-envelope ${styles.inputIcon}`} aria-hidden="true"/>
                <input id="email-input"
                       type="email"
                       className={`${styles.input} ${styles.inputWithIcon}`}
                       autoComplete="email"
                       aria-invalid={!!errors.profile}
                       value={email}
                       onChange={e => {
                         clearSectionError(PROFILE);
                         setEmail(e.target.value);
                       }}/>
              </div>
            </Field>
          </div>
        </section>

        <section ref={companyRef} className={styles.section} aria-labelledby="settings-company-title">
          <SectionHeader id="settings-company-title"
                         icon="fa-regular fa-building"
                         title="Company"
                         description="How your company appears across Vuuh."/>
          <div className={styles.sectionBody}>
            {errors.company && <SectionError message={errors.company}/>}
            {!canEditCompany && <div className={styles.notice}>
              <i className="fa-regular fa-circle-info" aria-hidden="true"/>
              Only company admins can change these details.
            </div>}
            <Field id="company-name-input" label="Company name">
              <div className={styles.inputWrap}>
                <i className={`fa-regular fa-building ${styles.inputIcon}`} aria-hidden="true"/>
                <input id="company-name-input"
                       type="text"
                       className={`${styles.input} ${styles.inputWithIcon}`}
                       autoComplete="organization"
                       disabled={!canEditCompany}
                       value={brandName}
                       onChange={e => {
                         clearSectionError(COMPANY);
                         setBrandName(e.target.value);
                       }}/>
              </div>
            </Field>
            <div className={styles.field}>
              <span className={styles.label}>Logo</span>
              <input ref={logoInputRef}
                     id="logo-input"
                     type="file"
                     accept="image/*"
                     className={styles.hiddenInput}
                     disabled={!canEditCompany}
                     onChange={e => {
                       onLogoFile(e.target.files ? e.target.files[0] : null);
                       e.target.value = "";
                     }}/>
              {logo
                ? <div className={styles.logoCard}>
                  <div className={styles.logoPreview}>
                    <img id="brand-logo" src={logo} alt={`${brandName} logo`}/>
                  </div>
                  <div className={styles.logoInfo}>
                    <strong>{logo !== saved.logo ? "New logo" : "Current logo"}</strong>
                    <span>Shown at up to 140 × 100 px.</span>
                    {canEditCompany && <div className={styles.logoActions}>
                      <button type="button" className={styles.smallButton} onClick={openLogoPicker}>
                        <i className="fa-regular fa-arrows-rotate" aria-hidden="true"/>Replace
                      </button>
                      <button type="button" className={styles.smallButton} onClick={() => setLogo(null)}>
                        <i className="fa-regular fa-trash-can" aria-hidden="true"/>Remove
                      </button>
                    </div>}
                  </div>
                </div>
                : <button type="button"
                          className={`${styles.dropzone} ${isDraggingLogo ? styles.dropzoneActive : ""}`}
                          disabled={!canEditCompany}
                          onClick={openLogoPicker}
                          onDragOver={e => {
                            e.preventDefault();
                            setIsDraggingLogo(true);
                          }}
                          onDragLeave={() => setIsDraggingLogo(false)}
                          onDrop={e => {
                            e.preventDefault();
                            setIsDraggingLogo(false);
                            onLogoFile(e.dataTransfer.files ? e.dataTransfer.files[0] : null);
                          }}>
                  <span className={styles.dropzoneIcon}><i className="fa-regular fa-image" aria-hidden="true"/></span>
                  <strong>{canEditCompany ? "Upload a logo" : "No logo yet"}</strong>
                  {canEditCompany && <span>Drag an image here, or click to select a file. The recommended size is 140 × 100 px.</span>}
                </button>}
            </div>
          </div>
        </section>

        <section ref={passwordRef} className={styles.section} aria-labelledby="settings-password-title">
          <SectionHeader id="settings-password-title"
                         icon="fa-regular fa-lock"
                         title="Password"
                         description="Leave these fields empty to keep your current password."/>
          <div className={styles.sectionBody}>
            {errors.password && <SectionError message={errors.password}/>}
            <div className={styles.fieldGrid}>
              <Field id="password-input" label="New password" hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}>
                <div className={styles.inputWrap}>
                  <i className={`fa-regular fa-key ${styles.inputIcon}`} aria-hidden="true"/>
                  <input id="password-input"
                         type={showPassword ? "text" : "password"}
                         className={`${styles.input} ${styles.inputWithIcon} ${styles.inputWithTrailing}`}
                         autoComplete="new-password"
                         value={password}
                         onChange={e => {
                           clearSectionError(PASSWORD);
                           setPassword(e.target.value);
                         }}/>
                  <button type="button"
                          className={styles.inputTrailing}
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? "Hide passwords" : "Show passwords"}
                          aria-pressed={showPassword}>
                    <i className={showPassword ? "fa-regular fa-eye-slash" : "fa-regular fa-eye"} aria-hidden="true"/>
                  </button>
                </div>
              </Field>
              <Field id="confirm-password-input" label="Confirm new password" hint={confirmHint}>
                <div className={`${styles.inputWrap} ${passwordsMismatch && errors.password ? styles.inputWrapInvalid : ""}`}>
                  <i className={`fa-regular fa-key ${styles.inputIcon}`} aria-hidden="true"/>
                  <input id="confirm-password-input"
                         type={showPassword ? "text" : "password"}
                         className={`${styles.input} ${styles.inputWithIcon}`}
                         autoComplete="new-password"
                         value={confirmPassword}
                         onChange={e => {
                           clearSectionError(PASSWORD);
                           setConfirmPassword(e.target.value);
                         }}/>
                </div>
              </Field>
            </div>
          </div>
        </section>
      </div>
    </div>

    <footer className={styles.footer}>
      <div aria-live="polite">{footerStatus}</div>
      <div className={styles.footerActions}>
        <button type="button" className={styles.secondaryButton} onClick={close} disabled={isSaving}>
          Close
        </button>
        <button type="button"
                id="submit-button"
                className={styles.primaryButton}
                disabled={!isDirty || isSaving}
                onClick={save}>
          {isSaving && <i className="fa-regular fa-spinner-third fa-spin" aria-hidden="true"/>}
          {isSaving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </footer>
  </Modal>
}

function SectionHeader({id, icon, title, description}: { id: string, icon: string, title: string, description: string }) {
  return <div className={styles.sectionHeader}>
    <span className={styles.sectionIcon}><i className={icon} aria-hidden="true"/></span>
    <div>
      <h3 id={id}>{title}</h3>
      <p>{description}</p>
    </div>
  </div>
}

function SectionError({message}: { message: string }) {
  return <div className={styles.sectionError} role="alert">
    <i className="fa-regular fa-circle-exclamation" aria-hidden="true"/>
    <span>{message}</span>
  </div>
}

function Field({id, label, hint, children}: { id: string, label: string, hint?: ReactNode, children: ReactNode }) {
  return <div className={styles.field}>
    <label htmlFor={id} className={styles.label}>{label}</label>
    {children}
    {hint && <div className={styles.hint}>{hint}</div>}
  </div>
}
